/* User settings — update first/last name (composes display_name). Owner-scoped */
/* by RLS ("profiles: self update"). */

import { useEffect, useState } from 'react';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { getMyProfileNames, updateMyProfileNames } from '../lib/profile';
import { useAuth } from '../hooks/use-auth';
import { getThemePref, setThemePref, type ThemePref } from '../lib/theme';

export default function Settings() {
  const { user } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [load, setLoad] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [theme, setTheme] = useState<ThemePref>(getThemePref());
  const pickTheme = (p: ThemePref) => { setTheme(p); setThemePref(p); };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoad('loading');
    (async () => {
      /* INVARIANT : `load` doit quitter « loading » quoi qu'il arrive. Un rejet
         ici (401, panne réseau, charge utile inattendue) laisserait la page sur
         « Chargement… » définitivement, sans message et sans issue. L'échec est
         un état visible et rattrapable, pas un formulaire vide : rendre le
         formulaire sur des champs jamais chargés ferait écraser le profil réel
         par des chaînes vides au premier enregistrement. */
      try {
        const p = await getMyProfileNames(user.id);
        if (cancelled) return;
        setFirstName(p.first_name ?? '');
        setLastName(p.last_name ?? '');
        setLoad('ready');
      } catch (err) {
        if (cancelled) return;
        setLoad('failed');
        console.error('[settings] load failed', (err as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [user, attempt]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setState('saving');
    try {
      await updateMyProfileNames(user.id, { firstName, lastName });
      setState('saved');
    } catch (err) {
      setState('error');
      console.error('[settings] save failed', (err as Error).message);
    }
  };

  return (
    <>
      <HeaderContent width="md" showBurger>
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Paramètres</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Gérez vos informations personnelles.
          </p>
        </div>
      </HeaderContent>
      <main className="max-w-[560px] mx-auto px-5 pb-16 pt-4">

        {load === 'loading' ? (
          <p className="text-muted-foreground py-4">Chargement…</p>
        ) : load === 'failed' ? (
          <div role="alert" className="py-4 flex flex-col items-start gap-3">
            <p className="m-0 text-destructive font-bold text-sm">
              Impossible de charger vos informations pour le moment.
            </p>
            <button
              type="button"
              onClick={() => setAttempt((n) => n + 1)}
              className="px-[18px] py-[10px] min-h-[44px] rounded-sm cursor-pointer font-[inherit] font-extrabold text-sm border-[1.5px] border-border bg-card text-foreground"
            >
              Réessayer
            </button>
          </div>
        ) : (
          <form onSubmit={save} className="mt-[22px] flex flex-col gap-4">
            <Field label="Prénom" value={firstName} onChange={(v) => { setFirstName(v); setState('idle'); }} placeholder="Votre prénom" />
            <Field label="Nom" value={lastName} onChange={(v) => { setLastName(v); setState('idle'); }} placeholder="Votre nom" />

            <div>
              <div className="text-xs font-bold text-muted-foreground mb-[6px]">Email</div>
              <div className="px-[14px] py-[11px] rounded-sm bg-secondary border border-border text-muted-foreground text-sm">
                {user?.email}
              </div>
            </div>

            <div className="flex items-center gap-[14px] mt-1">
              <button type="submit" disabled={state === 'saving'} className="px-[22px] py-3 min-h-[46px] rounded-sm border-none bg-naia-blue text-card font-extrabold text-[15px] cursor-pointer font-[inherit] shadow-card disabled:cursor-default disabled:opacity-70">
                {state === 'saving' ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              {state === 'saved' && <span className="text-muted-foreground font-bold text-[13px]">Enregistré ✓</span>}
              {state === 'error' && <span className="text-destructive font-bold text-[13px]">Erreur ⚠️</span>}
            </div>
          </form>
        )}

        <div className="mt-7 border-t border-border pt-[22px]">
          <h2 className="text-[16px] font-extrabold text-foreground">Apparence</h2>
          <div className="text-xs font-bold text-muted-foreground mt-3 mb-2">Thème</div>
          <div className="flex gap-2 flex-wrap">
            {([['system', 'Automatique'], ['light', 'Clair'], ['dark', 'Sombre']] as const).map(([val, label]) => (
              <button
                key={val}
                type="button"
                onClick={() => pickTheme(val)}
                aria-pressed={theme === val}
                className="px-[18px] py-[10px] min-h-[44px] rounded-sm cursor-pointer font-[inherit] font-extrabold text-sm border-[1.5px] border-border bg-card text-foreground aria-pressed:border-naia-blue aria-pressed:bg-naia-green-bg"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </main>
    </>
  );
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label className="block">
      <div className="text-xs font-bold text-muted-foreground mb-[6px]">{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-[14px] py-[11px] min-h-[46px] border border-border rounded-sm bg-card text-foreground text-[15px] font-[inherit]"
      />
    </label>
  );
}
